import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { productsCatalogService } from '../../services/catalogService';
import {
  productLinkedPartnersService,
  productLifecycleService,
  productUpdatesService,
} from '../../services/productProfileService';
import { useToast } from '../../contexts/ToastContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const getCategoryColor = (cat) => {
  const map = {
    networking: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    cloud: 'bg-purple-100 text-purple-700',
    software: 'bg-green-100 text-green-700',
    telecom: 'bg-orange-100 text-orange-700',
    iot: 'bg-cyan-100 text-cyan-700',
    hardware: 'bg-slate-100 text-slate-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return map?.[cat] || 'bg-slate-100 text-slate-600';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

const getMarginColor = (m) => {
  const map = { high: 'text-emerald-600', medium: 'text-amber-600', low: 'text-red-500' };
  return map?.[m] || 'text-slate-500';
};

const LIFECYCLE_STAGES = ['introduced', 'active', 'review', 'sunset'];

const STAGE_META = {
  introduced: { label: 'Introduced', icon: 'Sparkles', color: 'bg-blue-500', light: 'bg-blue-50 text-blue-700 border-blue-200' },
  active: { label: 'Active', icon: 'CheckCircle2', color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  review: { label: 'Under Review', icon: 'ClipboardList', color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  sunset: { label: 'Sunset', icon: 'Sunset', color: 'bg-slate-400', light: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const UPDATE_TYPE_META = {
  general: { label: 'General', color: 'bg-slate-100 text-slate-600', icon: 'Info' },
  price_update: { label: 'Price Update', color: 'bg-amber-100 text-amber-700', icon: 'DollarSign' },
  new_version: { label: 'New Version', color: 'bg-blue-100 text-blue-700', icon: 'RefreshCw' },
  eol_notice: { label: 'EOL Notice', color: 'bg-red-100 text-red-700', icon: 'AlertTriangle' },
};

const UPDATE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'price_update', label: 'Price Update' },
  { value: 'new_version', label: 'New Version' },
  { value: 'eol_notice', label: 'EOL Notice' },
];

const TIER_COLOR = {
  Strategic: 'bg-purple-100 text-purple-700',
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-slate-100 text-slate-600',
  Tactical: 'bg-blue-100 text-blue-700',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Post Update Modal ──────────────────────────────────────────────────────

function PostUpdateModal({ productId, onClose, onSuccess }) {
  const [form, setForm] = useState({ updateType: 'general', title: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await productUpdatesService?.create(productId, form);
      onSuccess(created);
    } catch (err) {
      setError(err?.message || 'Failed to post update.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="flex items-center gap-12">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="Megaphone" size={16} className="text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Post Product Update</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-16">
          {error && (
            <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={15} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Update Type</label>
            <select
              value={form?.updateType}
              onChange={e => setForm(p => ({ ...p, updateType: e?.target?.value }))}
              className="w-full px-12 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
            >
              {UPDATE_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Title *</label>
            <input
              value={form?.title}
              onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
              placeholder="e.g., Firmware v2.1 Released"
              className="w-full px-12 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Details</label>
            <textarea
              value={form?.content}
              onChange={e => setForm(p => ({ ...p, content: e?.target?.value }))}
              placeholder="Describe the update in detail..."
              rows={4}
              className="w-full px-12 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            />
          </div>
          <div className="flex items-center gap-12 pt-4">
            <Button variant="default" type="submit" disabled={saving}>
              {saving ? 'Posting...' : 'Post Update'}
            </Button>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function ProductProfile() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [product, setProduct] = useState(null);
  const [linkedPartners, setLinkedPartners] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unassignConfirm, setUnassignConfirm] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [activeTab, setActiveTab] = useState('specs');

  const loadAll = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [prod, partners, lc, upd] = await Promise.all([
        productsCatalogService?.getAll()?.then(all => all?.find(p => p?.id === productId)),
        productLinkedPartnersService?.getByProduct(productId),
        productLifecycleService?.getByProduct(productId),
        productUpdatesService?.getByProduct(productId),
      ]);
      if (!prod) { navigate('/products-catalog'); return; }
      setProduct(prod);
      setLinkedPartners(partners || []);
      setLifecycle(lc || []);
      setUpdates(upd || []);
    } catch (err) {
      toast?.error('Failed to load product profile.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId, navigate, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUnassign = async (linkId) => {
    try {
      await productLinkedPartnersService?.unlink(linkId);
      setLinkedPartners(prev => prev?.filter(p => p?.id !== linkId));
      toast?.success('Partner unassigned from product.');
    } catch (err) {
      toast?.error('Failed to unassign partner.');
    } finally {
      setUnassignConfirm(null);
    }
  };

  const handlePostSuccess = (newUpdate) => {
    setUpdates(prev => [newUpdate, ...prev]);
    setShowPostModal(false);
    toast?.success('Update posted successfully.');
  };

  const currentStage = lifecycle?.length > 0 ? lifecycle?.[lifecycle?.length - 1]?.stage : null;
  const currentStageIndex = LIFECYCLE_STAGES?.indexOf(currentStage);

  const TABS = [
    { id: 'specs', label: 'Specifications', icon: 'FileText' },
    { id: 'partners', label: `Linked Partners (${linkedPartners?.length})`, icon: 'Users' },
    { id: 'lifecycle', label: 'Lifecycle', icon: 'GitBranch' },
    { id: 'updates', label: `Updates (${updates?.length})`, icon: 'Megaphone' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/20">
        <Header />
        <main className="pt-[148px] flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-12 text-slate-500">
            <Icon name="Loader2" size={22} className="animate-spin" />
            <span>Loading product profile...</span>
          </div>
        </main>
      </div>
    );
  }

  if (!product) return null;

  return (
    <>
      <Helmet>
        <title>{product?.productName} — Product Profile | PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/20">
        <Header />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1400px] mx-auto">

            {/* Breadcrumb */}
            <nav className="flex items-center gap-8 text-sm text-slate-500 mb-24">
              <Link to="/products-catalog" className="hover:text-primary transition-colors flex items-center gap-6">
                <Icon name="Package" size={14} />
                Products Catalog
              </Link>
              <Icon name="ChevronRight" size={14} className="text-slate-300" />
              <span className="text-slate-700 font-medium">{product?.productName}</span>
            </nav>

            {/* ── Product Header ─────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28 mb-24">
              <div className="flex flex-col lg:flex-row lg:items-start gap-24">
                {/* Icon + Identity */}
                <div className="flex items-start gap-20 flex-1">
                  <div className="p-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg flex-shrink-0">
                    <Icon name="Package" size={32} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-10 mb-8">
                      <span className={`inline-flex px-10 py-4 rounded-md text-xs font-semibold capitalize ${getCategoryColor(product?.category)}`}>
                        {product?.category || 'Uncategorized'}
                      </span>
                      <span className={`inline-flex px-10 py-4 rounded-full text-xs font-semibold capitalize ${getStatusColor(product?.catalogStatus)}`}>
                        {product?.catalogStatus}
                      </span>
                      {currentStage && (
                        <span className={`inline-flex items-center gap-5 px-10 py-4 rounded-full text-xs font-semibold border ${STAGE_META?.[currentStage]?.light}`}>
                          <Icon name={STAGE_META?.[currentStage]?.icon} size={11} />
                          {STAGE_META?.[currentStage]?.label}
                        </span>
                      )}
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-6">{product?.productName}</h1>
                    <div className="flex flex-wrap items-center gap-16 text-sm text-slate-500">
                      {product?.oemBrand && (
                        <span className="flex items-center gap-6">
                          <Icon name="Building2" size={14} className="text-slate-400" />
                          {product?.oemBrand}
                        </span>
                      )}
                      {product?.modelSku && (
                        <span className="flex items-center gap-6 font-mono text-xs bg-slate-100 px-8 py-3 rounded-md text-slate-600">
                          {product?.modelSku}
                        </span>
                      )}
                      {product?.licensingModel && (
                        <span className="flex items-center gap-6">
                          <Icon name="Key" size={14} className="text-slate-400" />
                          {product?.licensingModel}
                        </span>
                      )}
                    </div>
                    {product?.description && (
                      <p className="text-sm text-slate-500 mt-12 leading-relaxed max-w-2xl">{product?.description}</p>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-12 lg:w-auto lg:flex-shrink-0">
                  {[
                    { label: 'Partners Linked', value: linkedPartners?.length, icon: 'Users', color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Active Links', value: linkedPartners?.filter(p => p?.linkStatus === 'active')?.length, icon: 'CheckCircle2', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Avg Margin', value: linkedPartners?.length > 0 ? `${Math.round(linkedPartners?.reduce((s, p) => s + (Number(p?.marginPct) || 0), 0) / linkedPartners?.length)}%` : '—', icon: 'TrendingUp', color: 'text-amber-600', bg: 'bg-amber-50' },
                  ]?.map(stat => (
                    <div key={stat?.label} className="text-center p-16 rounded-xl border border-slate-100 bg-slate-50/60">
                      <div className={`w-10 h-10 rounded-xl ${stat?.bg} flex items-center justify-center mx-auto mb-8`}>
                        <Icon name={stat?.icon} size={18} className={stat?.color} />
                      </div>
                      <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
                      <p className="text-xs text-slate-500 mt-2 leading-tight">{stat?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Tabs ──────────────────────────────────────────────────── */}
            <div className="flex items-center gap-4 mb-24 border-b border-slate-200 overflow-x-auto">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-8 px-16 py-12 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* ── Specifications Tab ─────────────────────────────────────── */}
            {activeTab === 'specs' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28">
                <h2 className="text-base font-bold text-slate-900 mb-20 flex items-center gap-10">
                  <Icon name="FileText" size={18} className="text-primary" />
                  Full Specifications
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                  {[
                    { label: 'Product Name', value: product?.productName, icon: 'Package' },
                    { label: 'Category', value: product?.category, icon: 'Tag' },
                    { label: 'OEM / Brand', value: product?.oemBrand, icon: 'Building2' },
                    { label: 'Model / SKU', value: product?.modelSku, icon: 'Hash', mono: true },
                    { label: 'Licensing Model', value: product?.licensingModel, icon: 'Key' },
                    { label: 'Target Industry', value: product?.targetIndustry, icon: 'Briefcase' },
                    { label: 'Price Range', value: product?.priceRange ? product?.priceRange?.replace(/\$/g, 'OMR ') : null, icon: 'DollarSign' },
                    { label: 'Margin Category', value: product?.marginCategory, icon: 'TrendingUp', colored: true },
                    { label: 'Catalog Status', value: product?.catalogStatus, icon: 'Activity' },
                  ]?.map(spec => (
                    <div key={spec?.label} className="p-20">
                      <div className="flex items-center gap-8 mb-6">
                        <Icon name={spec?.icon} size={13} className="text-slate-400" />
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{spec?.label}</span>
                      </div>
                      <p className={`text-sm font-semibold ${spec?.colored ? getMarginColor(spec?.value) : 'text-slate-800'} ${spec?.mono ? 'font-mono' : ''} capitalize`}>
                        {spec?.value || '—'}
                      </p>
                    </div>
                  ))}
                </div>
                {product?.description && (
                  <div className="mt-20 pt-20 border-t border-slate-100">
                    <div className="flex items-center gap-8 mb-8">
                      <Icon name="AlignLeft" size={13} className="text-slate-400" />
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Description</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{product?.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Linked Partners Tab ────────────────────────────────────── */}
            {activeTab === 'partners' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100">
                  <h2 className="text-base font-bold text-slate-900 flex items-center gap-10">
                    <Icon name="Users" size={18} className="text-primary" />
                    Linked Partners
                    <span className="ml-4 px-8 py-3 bg-slate-100 rounded-full text-xs font-semibold text-slate-600">
                      {linkedPartners?.length}
                    </span>
                  </h2>
                </div>
                {linkedPartners?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-56 text-slate-400">
                    <Icon name="Users" size={36} className="mb-12 opacity-30" />
                    <p className="font-medium">No partners linked yet</p>
                    <p className="text-sm mt-4">Assign this product to partners from their profile pages</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          {['Partner', 'Tier', 'Authorization', 'Margin %', 'Strategic', 'Status', 'Linked On', 'Action']?.map(h => (
                            <th key={h} className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {linkedPartners?.map(lp => (
                          <tr key={lp?.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-16 py-14">
                              <Link
                                to={`/partner-profile-portal/${lp?.partnerId}`}
                                className="text-sm font-semibold text-primary hover:underline"
                              >
                                {lp?.partnerName}
                              </Link>
                              {lp?.partnerCountry && (
                                <p className="text-xs text-slate-400 mt-1">{lp?.partnerCountry}</p>
                              )}
                            </td>
                            <td className="px-16 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-md text-xs font-semibold ${TIER_COLOR?.[lp?.partnerTier] || 'bg-slate-100 text-slate-600'}`}>
                                {lp?.partnerTier || '—'}
                              </span>
                            </td>
                            <td className="px-16 py-14 text-sm text-slate-600 capitalize">{lp?.authorizationLevel || '—'}</td>
                            <td className="px-16 py-14">
                              <span className={`text-sm font-bold ${getMarginColor(lp?.marginPct >= 20 ? 'high' : lp?.marginPct >= 10 ? 'medium' : 'low')}`}>
                                {lp?.marginPct != null ? `${lp?.marginPct}%` : '—'}
                              </span>
                            </td>
                            <td className="px-16 py-14">
                              {lp?.isStrategic ? (
                                <span className="inline-flex items-center gap-4 px-8 py-4 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                  <Icon name="Star" size={11} />Strategic
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-16 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-full text-xs font-semibold capitalize ${lp?.linkStatus === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                {lp?.linkStatus || '—'}
                              </span>
                            </td>
                            <td className="px-16 py-14 text-sm text-slate-500">{formatDate(lp?.createdAt)}</td>
                            <td className="px-16 py-14">
                              {unassignConfirm === lp?.id ? (
                                <div className="flex items-center gap-6">
                                  <button
                                    onClick={() => handleUnassign(lp?.id)}
                                    className="px-8 py-4 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setUnassignConfirm(null)}
                                    className="px-8 py-4 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setUnassignConfirm(lp?.id)}
                                  className="flex items-center gap-5 px-10 py-6 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                                >
                                  <Icon name="UserMinus" size={13} />
                                  Unassign
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Lifecycle Tab ──────────────────────────────────────────── */}
            {activeTab === 'lifecycle' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28">
                <h2 className="text-base font-bold text-slate-900 mb-24 flex items-center gap-10">
                  <Icon name="GitBranch" size={18} className="text-primary" />
                  Product Lifecycle
                </h2>

                {/* Stage Progress Bar */}
                <div className="mb-32">
                  <div className="flex items-center gap-0 relative">
                    {LIFECYCLE_STAGES?.map((stage, idx) => {
                      const meta = STAGE_META?.[stage];
                      const isCompleted = currentStageIndex > idx;
                      const isCurrent = currentStageIndex === idx;
                      const isUpcoming = currentStageIndex < idx;
                      return (
                        <React.Fragment key={stage}>
                          <div className="flex flex-col items-center flex-1 relative z-10">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                              isCurrent ? `${meta?.color} border-transparent shadow-lg shadow-${meta?.color}/30` :
                              isCompleted ? 'bg-slate-300 border-slate-300' : 'bg-white border-slate-200'
                            }`}>
                              <Icon
                                name={isCurrent ? meta?.icon : isCompleted ? 'Check' : 'Circle'}
                                size={16}
                                className={isCurrent ? 'text-white' : isCompleted ? 'text-white' : 'text-slate-300'}
                              />
                            </div>
                            <p className={`text-xs font-semibold mt-8 text-center ${isCurrent ? 'text-slate-900' : isCompleted ? 'text-slate-500' : 'text-slate-300'}`}>
                              {meta?.label}
                            </p>
                          </div>
                          {idx < LIFECYCLE_STAGES?.length - 1 && (
                            <div className={`h-0.5 flex-1 -mt-6 ${isCompleted || isCurrent ? 'bg-slate-300' : 'bg-slate-100'}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Stage History */}
                {lifecycle?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-32 text-slate-400">
                    <Icon name="GitBranch" size={32} className="mb-10 opacity-30" />
                    <p className="font-medium text-sm">No lifecycle history recorded</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-16">Stage History</h3>
                    <div className="relative">
                      <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-slate-100" />
                      <div className="space-y-0">
                        {lifecycle?.map((entry, idx) => {
                          const meta = STAGE_META?.[entry?.stage] || STAGE_META?.active;
                          const isLast = idx === lifecycle?.length - 1;
                          return (
                            <div key={entry?.id} className="flex items-start gap-16 pb-20 relative">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 ${isLast ? meta?.color : 'bg-slate-200'}`}>
                                <Icon name={meta?.icon} size={14} className="text-white" />
                              </div>
                              <div className="flex-1 bg-slate-50 rounded-xl p-16 border border-slate-100">
                                <div className="flex items-center justify-between mb-6">
                                  <span className={`inline-flex items-center gap-5 px-8 py-3 rounded-full text-xs font-semibold border ${meta?.light}`}>
                                    {meta?.label}
                                  </span>
                                  <span className="text-xs text-slate-400">{formatDate(entry?.stageDate)}</span>
                                </div>
                                {entry?.notes && <p className="text-sm text-slate-600 mt-6">{entry?.notes}</p>}
                                <div className="flex items-center gap-12 mt-8 text-xs text-slate-400">
                                  <span className="flex items-center gap-4">
                                    <Icon name="User" size={11} />
                                    {entry?.changedByName}
                                  </span>
                                  {entry?.expectedEolDate && (
                                    <span className="flex items-center gap-4 text-amber-600">
                                      <Icon name="Calendar" size={11} />
                                      EOL: {formatDate(entry?.expectedEolDate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Updates Tab ────────────────────────────────────────────── */}
            {activeTab === 'updates' && (
              <div className="space-y-0">
                <div className="flex items-center justify-between mb-20">
                  <h2 className="text-base font-bold text-slate-900 flex items-center gap-10">
                    <Icon name="Megaphone" size={18} className="text-primary" />
                    Updates & News
                  </h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => setShowPostModal(true)}
                  >
                    Post Update
                  </Button>
                </div>

                {updates?.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center py-56 text-slate-400">
                    <Icon name="Megaphone" size={36} className="mb-12 opacity-30" />
                    <p className="font-medium">No updates posted yet</p>
                    <p className="text-sm mt-4">Post the first update for this product</p>
                  </div>
                ) : (
                  <div className="space-y-12">
                    {updates?.map(upd => {
                      const meta = UPDATE_TYPE_META?.[upd?.updateType] || UPDATE_TYPE_META?.general;
                      return (
                        <div key={upd?.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                          <div className="flex items-start justify-between gap-16">
                            <div className="flex items-start gap-14 flex-1">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta?.color}`}>
                                <Icon name={meta?.icon} size={15} />
                              </div>
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-10 mb-6">
                                  <span className={`inline-flex px-8 py-3 rounded-full text-xs font-semibold ${meta?.color}`}>
                                    {meta?.label}
                                  </span>
                                  <span className="text-xs text-slate-400">{formatDate(upd?.createdAt)}</span>
                                  <span className="text-xs text-slate-400 flex items-center gap-4">
                                    <Icon name="User" size={11} />
                                    {upd?.authorName}
                                  </span>
                                </div>
                                <h3 className="text-sm font-bold text-slate-900 mb-6">{upd?.title}</h3>
                                {upd?.content && (
                                  <p className="text-sm text-slate-600 leading-relaxed">{upd?.content}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
      {showPostModal && (
        <PostUpdateModal
          productId={productId}
          onClose={() => setShowPostModal(false)}
          onSuccess={handlePostSuccess}
        />
      )}
    </>
  );
}
